import heic2any from "heic2any";

const HEIC_EXT_RE = /\.(heic|heif)$/i;
const JFIF_EXT_RE = /\.jfif$/i;

function isHeicFile(file) {
  if (!(file instanceof File)) return false;
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return (
    type === "image/heic" ||
    type === "image/heif" ||
    HEIC_EXT_RE.test(name)
  );
}

function isJfifFile(file) {
  if (!(file instanceof File)) return false;
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return (
    (type === "image/jpeg" && JFIF_EXT_RE.test(name)) ||
    type === "image/jfif" ||
    JFIF_EXT_RE.test(name)
  );
}

/**
 * Convert a JFIF file to standard JPEG format.
 * JFIF files are already JPEG, we just need to rename the extension.
 * 
 * @param {File} file
 * @returns {Promise<File>}
 */
async function convertJfifToJpeg(file) {
  if (!isJfifFile(file)) return file;

  // JFIF is already JPEG format, just read it and create a new File with .jpg extension
  const blob = await file.arrayBuffer();
  const newName = (file.name || "image.jfif").replace(JFIF_EXT_RE, ".jpg");

  return new File([blob], newName, {
    type: "image/jpeg",
    lastModified: file.lastModified || Date.now(),
  });
}

/**
 * Convert a single HEIC/HEIF/JFIF File to a JPEG File.
 * If the file is not HEIC/HEIF/JFIF, returns it unchanged.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function convertHeicToJpeg(file) {
  // First check if it's HEIC/HEIF
  if (isHeicFile(file)) {
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.92,
    });

    const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
    const newName = (file.name || "image.heic").replace(HEIC_EXT_RE, ".jpg");

    return new File([jpegBlob], newName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  }

  // Then check if it's JFIF
  return await convertJfifToJpeg(file);
}

/**
 * Convert a list of Files, converting HEIC/HEIF/JFIF -> JPEG.
 * Non-HEIC/JFIF files are returned unchanged.
 *
 * @param {File[]} files
 * @returns {Promise<File[]>}
 */
export async function convertHeicFiles(files) {
  const input = Array.isArray(files) ? files : [];
  const results = [];

  for (const f of input) {
    results.push(await convertHeicToJpeg(f));
  }

  return results;
}

